/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useResponsive.js
 *
 * Purpose: Provides current-viewport breakpoint booleans and gate components for conditionally
 *     rendering children by breakpoint.
 *
 * Scope: responsive layout across the customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Uses react-responsive's useMediaQuery against the shared breakpoints config to
 *     compute isMobileOnly, isMobile, isTablet, isDesktop, and isWideScreen flags. Memoizes four
 *     small components (Mobile, Tablet, Desktop, WideScreen) that render their children only
 *     when the corresponding flag is true, and returns all flags and components together in a
 *     single memoized object.
 *
 * Exports: default useResponsive hook (breakpoint flags plus Mobile/Tablet/Desktop/WideScreen
 *     render-gate components).
 */

import { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from 'shared/theme/theme';

const useResponsive = () => {
    const isMobileOnly = useMediaQuery({
        maxWidth: breakpoints.mobileOnly - 1,
    });
    const isMobile = useMediaQuery({ maxWidth: breakpoints.mobile - 1 });
    const isTablet = useMediaQuery({
        maxWidth: breakpoints.tablet - 1,
    });
    const isDesktop = useMediaQuery({ minWidth: breakpoints.tablet });
    const isWideScreen = useMediaQuery({ minWidth: breakpoints.wideScreen });

    const Mobile = useMemo(() => ({ children }) => {
        return isMobile ? children : null;
    }, [isMobile]);

    const Tablet = useMemo(() => ({ children }) => {
        return isTablet ? children : null;
    }, [isTablet]);

    const Desktop = useMemo(() => ({ children }) => {
        return isDesktop ? children : null;
    }, [isDesktop]);

    const WideScreen = useMemo(() => ({ children }) => {
        return isWideScreen ? children : null;
    }, [isWideScreen]);

    return useMemo(() => ({
        Mobile,
        Tablet,
        Desktop,
        WideScreen,
        isMobileOnly,
        isMobile,
        isTablet,
        isDesktop,
        isWideScreen
    }), [Mobile, Tablet, Desktop, WideScreen, isMobileOnly, isMobile, isTablet, isDesktop, isWideScreen]);
};

export default useResponsive;
